// Étape « Pièces » du guichet : pour chaque pièce attendue selon les réponses du formulaire,
// dépôt du fichier présenté par l'usager (POST /documents, rattaché au dossier au dépôt) ;
// pièces supplémentaires possibles. Les fichiers ne sont pas conservés dans le brouillon.
import type { DocumentResume } from '@cc/contrats/ui-metier';
import { Bandeau, Bouton, FichierChamp, Selection, tailleLisible } from '@cc/ui';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { CaptureDocument } from '../../../capture/CaptureDocument';
import { useSession } from '../../../session/FournisseurSession';
import { requete } from '../../../api/client';
import { messageErreur } from '../../../api/erreurs';
import { TAILLE_MAX_OCTETS, TYPES_ACCEPTES } from '../listes';
import {
  piecesAttendues,
  type PieceDeposee,
  type SaisieGuichet,
  type VersionGuichet,
} from './etapes';
import commun from '../../../commun/commun.module.css';
import styles from '../metier.module.css';

async function deposerPiece(fichier: File, titre: string): Promise<DocumentResume> {
  const f = new FormData();
  f.set('fichier', fichier);
  f.set('titre', titre);
  f.set('type_document', 'piece_dossier');
  return requete<DocumentResume>('/documents', { methode: 'POST', corps: f });
}

function DepotPiece({
  libelle,
  titre,
  type,
  captureId,
  onDeposee,
}: {
  captureId?: string;
  libelle: string;
  titre: string;
  type: string;
  onDeposee: (p: PieceDeposee) => void;
}) {
  const { contexte } = useSession();
  const [fichiers, setFichiers] = useState<File[]>([]);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const envoyer = async () => {
    const f = fichiers[0];
    if (!f) return;
    setEnCours(true);
    setErreur(null);
    try {
      const d = await deposerPiece(f, `${titre} : ${f.name}`);
      onDeposee({ document_id: d.id, type, titre: d.titre });
      setFichiers([]);
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  };
  return (
    <div className={styles.pile}>
      <FichierChamp
        libelle={libelle}
        fichiers={fichiers}
        onFichiers={setFichiers}
        multiple={false}
        accept={TYPES_ACCEPTES}
        tailleMax={TAILLE_MAX_OCTETS}
        erreur={erreur}
      />
      {captureId && contexte?.appartenance_active ? (
        <CaptureDocument
          compteId={contexte.compte.id}
          espaceId={contexte.appartenance_active.id}
          usage={'guichet:' + captureId + ':' + type}
          disabled={enCours}
          onFichier={(f) => setFichiers([f])}
        />
      ) : null}
      {fichiers.length > 0 ? (
        <div>
          <Bouton variante="secondaire" petit chargement={enCours} onClick={() => void envoyer()}>
            Déposer {fichiers[0]?.name} ({tailleLisible(fichiers[0]?.size ?? 0)})
          </Bouton>
        </div>
      ) : null}
    </div>
  );
}

export function EtapePieces({
  version,
  saisie,
  pieces,
  onChanger,
}: {
  version: VersionGuichet;
  saisie: SaisieGuichet;
  pieces: PieceDeposee[];
  onChanger: (p: PieceDeposee[]) => void;
}) {
  const attendues = piecesAttendues(version, saisie.donnees);
  const [typeLibre, setTypeLibre] = useState('');
  const fourniesParType = new Map<string, PieceDeposee[]>();
  for (const p of pieces) fourniesParType.set(p.type, [...(fourniesParType.get(p.type) ?? []), p]);
  const retirer = (id: string) => onChanger(pieces.filter((p) => p.document_id !== id));
  return (
    <div className={styles.pile}>
      {attendues.length === 0 ? (
        <p className={commun.na}>
          Aucune pièce n'est demandée pour cette démarche avec ces réponses.
        </p>
      ) : null}
      <ul className={styles.listePieces}>
        {attendues.map((p) => {
          const fournies = fourniesParType.get(p.type) ?? [];
          return (
            <li key={p.type} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
              <span>
                <strong>{p.libelle}</strong> {p.obligatoire ? '(obligatoire)' : '(facultative)'}
                {p.aide ? <span className={commun.na}> : {p.aide}</span> : null}
              </span>
              {fournies.map((f) => (
                <span key={f.document_id} className={commun.ligne}>
                  <span>Déposée : {f.titre}</span>
                  <Bouton
                    petit
                    variante="discret"
                    icone={Trash2}
                    aria-label={`Retirer ${f.titre}`}
                    onClick={() => retirer(f.document_id)}
                  >
                    Retirer
                  </Bouton>
                </span>
              ))}
              {fournies.length === 0 ? (
                <DepotPiece
                  captureId={
                    saisie.capture_id
                      ? `${saisie.capture_id}:${saisie.demarche_version_id}`
                      : undefined
                  }
                  libelle={`Fichier : ${p.libelle}`}
                  titre={p.libelle}
                  type={p.type}
                  onDeposee={(d) => onChanger([...pieces, d])}
                />
              ) : null}
            </li>
          );
        })}
      </ul>
      {version.pieces.length > 0 ? (
        <details>
          <summary>Pièce supplémentaire</summary>
          <div className={styles.pile} style={{ paddingTop: 8 }}>
            <Selection
              libelle="Type de la pièce supplémentaire"
              value={typeLibre}
              optionVide="Choisir un type"
              options={version.pieces.map((p) => ({ valeur: p.type, libelle: p.libelle }))}
              onChange={(e) => setTypeLibre(e.target.value)}
            />
            {typeLibre ? (
              <DepotPiece
                captureId={
                  saisie.capture_id
                    ? `${saisie.capture_id}:${saisie.demarche_version_id}`
                    : undefined
                }
                libelle="Fichier"
                titre={version.pieces.find((p) => p.type === typeLibre)?.libelle ?? typeLibre}
                type={typeLibre}
                onDeposee={(d) => {
                  onChanger([...pieces, d]);
                  setTypeLibre('');
                }}
              />
            ) : null}
          </div>
        </details>
      ) : null}
      {pieces.length > 0 ? (
        <Bandeau type="info">
          Les fichiers déposés sont déjà enregistrés comme documents ; ils seront rattachés au
          dossier au moment du dépôt. Un brouillon repris plus tard ne les conserve pas.
        </Bandeau>
      ) : null}
    </div>
  );
}
